/**
 * Single source of truth for the customer-send layer enums (plan D-8):
 * channels, send/delivery statuses, and contact preferences. These live as
 * TEXT columns validated at the boundary from THIS registry — never PG enums
 * (pg-mem cannot model enums and migrations stay append-only-simple).
 *
 * Leaf module: imports NOTHING (shared <- engine <- server import direction,
 * same rule as addons-schema.ts).
 */

/**
 * Send channels. 'whatsapp' is an interface slot only (P1.6): the transport
 * shape is channel-pluggable now, the WA utility-template driver lands after
 * Meta verification + template approval — a config change, not a redesign.
 *
 * T549 M1 adds 'email' as the second CUSTOMER channel. Appended, never
 * re-ordered: send_ledger.channel is plain TEXT holding these values, so the
 * list is append-only history, not a free-form enum.
 *
 * T660 M1 appends 'link', and it is NOT a transport. It records that a deposit
 * URL was handed to the OPERATOR's clipboard (POST /api/bookings/:id/payment-link)
 * - see CHANNEL_LINK below for why that needed a member of its own rather than
 * borrowing 'whatsapp'. Being in THIS list is exactly what it means: ledger
 * history. It is deliberately absent from IMPLEMENTED_CHANNELS, so it can never
 * be requested as a customer send.
 */
/**
 * T1988 appends 'team_email': the deposit-paid OPERATOR alert delivered by
 * email (notify/deposit-paid-notify.ts, one row per staff address). The
 * transport is Resend, exactly as for CHANNEL_EMAIL, and that is the point of
 * the separate member: the timeline reader (bookings-repo.ts sendKind)
 * classifies a non-quote row by its channel, so a staff alert filed under
 * 'email' rendered to Helen as "Customer message sent" - a false audit record
 * that the customer had been contacted. Like 'link', this member names WHO the
 * row reached, is ledger history only, and is absent from IMPLEMENTED_CHANNELS
 * so it can never be requested as a customer send.
 */
export const CHANNELS = ['sms', 'whatsapp', 'email', 'link', 'team_email'] as const;
export type Channel = (typeof CHANNELS)[number];

/**
 * Channels a customer-send transport driver exists for.
 *
 * T549 M6 gave this a RUNTIME READER: handleSendQuote refuses (400) a requested
 * channel that is not in this list, which is what stops 'whatsapp' — a real
 * ledger channel for operator alerts, with no customer-send driver — being
 * accepted by `isChannel` alone. So adding a member here is no longer a
 * declaration: it makes that channel requestable, and the send path must be able
 * to drive it. (Until M6 nothing but its own spec read this constant, which is
 * why M1 could register 'email' ahead of its transport.)
 *
 * T549 M7 adds a SECOND runtime reader, on the other side of the wire: the
 * operator channel picker (client/components/SendQuote.tsx) offers exactly this
 * list, filtered to the channels that have a recipient on file. So a member
 * added here becomes both requestable AND offerable in one edit.
 *
 * It is an ADMISSIBILITY list, not an enable gate. Whether a channel can
 * actually send is still config presence at boot (D-6), per channel: an
 * admissible channel whose transport is unconfigured is a 503, not a 400.
 */
export const IMPLEMENTED_CHANNELS = ['sms', 'email'] as const satisfies readonly Channel[];

export function isChannel(v: unknown): v is Channel {
  return typeof v === 'string' && (CHANNELS as readonly string[]).includes(v);
}

/**
 * send_ledger.delivery_status lifecycle. 'composed' and 'send_failed' are OUR
 * bookend states (row written at compose time / transport call errored); the
 * middle seven are the Twilio Message lifecycle verbatim (research E9:
 * accepted -> queued -> sending -> sent -> delivered | undelivered | failed)
 * so callback MessageStatus values map 1:1 with no translation layer.
 *
 * T524 appends 'copied' — a THIRD bookend of our own, for the manual route
 * where Helen copies the customer message to her clipboard and pastes it into
 * WhatsApp herself. Appended, never re-ordered: send_ledger.delivery_status is
 * plain TEXT holding history, stated at the top of this file.
 */
export const SEND_STATUSES = [
  'composed',
  'accepted',
  'queued',
  'sending',
  'sent',
  'delivered',
  'undelivered',
  'failed',
  'send_failed',
  'copied',
] as const;
export type SendStatus = (typeof SEND_STATUSES)[number];

export function isSendStatus(v: unknown): v is SendStatus {
  return typeof v === 'string' && (SEND_STATUSES as readonly string[]).includes(v);
}

/**
 * Named write-values for the send path (T367 item 2). The ledger columns are
 * plain TEXT (NewSend.channel/deliveryStatus are `string`), so a bare literal at
 * a write site is unchecked — a typo compiles and lands in the DB. Annotating
 * each const against its registry type makes `tsc` the guard: a value that isn't
 * in CHANNELS/SEND_STATUSES fails the build, and a rename of the underlying
 * value is caught at every write site instead of silently drifting.
 *
 * Deliberately NOT `IMPLEMENTED_CHANNELS[0]` — index-into-tuple is fragile the
 * moment a second channel ships.
 */
export const CHANNEL_SMS: Channel = 'sms';
/**
 * Operator alerts ride the WhatsApp client (Vlad, 2026-07-19: customers on
 * SMS, operators on WhatsApp). Note this is the LEDGER channel, recording what
 * transport an alert actually went out on -- distinct from
 * IMPLEMENTED_CHANNELS, which is about the customer-send driver.
 */
export const CHANNEL_WHATSAPP: Channel = 'whatsapp';
/** The second CUSTOMER channel (T549 M6): Resend, wired into the send path. */
export const CHANNEL_EMAIL: Channel = 'email';
/**
 * THE OPERATOR-CLIPBOARD DISCLOSURE OF A DEPOSIT URL (T660 M1). Not a transport:
 * nothing was transmitted to anybody. The paired row is
 * (channel 'link', target 'clipboard', delivery_status 'copied', quote_id NULL,
 * message_ref NULL) written by handlePostPaymentLink, and it is the audit half
 * of that route's compensating controls (the throttle is the other half).
 *
 * WHY A NEW MEMBER rather than reusing CHANNEL_WHATSAPP as the copy route does.
 * The row surfaces in the operator timeline, which renders
 * `channelLabel(registry, entry.channel)` (client/screens/BookingDetail.tsx), so
 * a bare-URL clipboard copy filed under 'whatsapp' would read "WhatsApp" to
 * Helen - a false audit record about a message nobody composed, on the one row
 * whose entire job is to be true. CHANNELS is documented above as append-only
 * ledger history and is explicitly distinct from IMPLEMENTED_CHANNELS (the
 * customer-send driver list), which is why appending here ships no driver:
 * handleSendQuote refuses any channel outside IMPLEMENTED_CHANNELS with a 400.
 */
export const CHANNEL_LINK: Channel = 'link';
/**
 * THE OPERATOR DEPOSIT-PAID ALERT BY EMAIL (T1988). Same Resend transport as
 * CHANNEL_EMAIL, different AUDIENCE, and the audience is what the ledger row
 * has to carry: see the CHANNELS docblock. Written only by
 * notify/deposit-paid-notify.ts's email leg.
 */
export const CHANNEL_TEAM_EMAIL: Channel = 'team_email';
/**
 * THE EXPLICIT OPERATOR-ALERT MARKER. A ledger row on one of these channels
 * reached the TEAM (Owen, Helen), never a customer, whatever its other columns
 * say. bookings-repo.ts sendKind reads this set - and nothing else - to decide
 * between 'team_alert_sent' and 'customer_message_sent' for a non-quote row.
 * Neither `quote_id` nor `payment_id` can carry that fact: the customer
 * deposit-received confirmation (notify/deposit-received-notify.ts) writes both
 * NULL exactly as the operator alerts do. Adding an operator channel means
 * adding it HERE, or its rows will read as customer messages.
 */
export const TEAM_ALERT_CHANNELS: ReadonlySet<Channel> = new Set<Channel>([CHANNEL_WHATSAPP, CHANNEL_TEAM_EMAIL]);
/**
 * The PENDING-FIRST row (T608 M3b, R6c): written BEFORE the transport is
 * touched, so a send whose process dies mid-call is still visible instead of
 * being a silent hang. Deliberately the EXISTING `SEND_STATUSES[0]` -
 * documented above as "row written at compose time", already carrying an
 * operator label (SEND_STATUS_LABELS.composed = 'Composed'), and deliberately
 * ABSENT from CALLBACK_STATUSES so no provider callback can move a row back
 * into it. A new 'pending' member would widen the registry, break `isSendStatus`
 * at the boundary and need a label, for nothing this value does not already say.
 *
 * It is NOT proof of delivery, and no read path may treat it as such: it means
 * "we were about to send this", full stop. It is in neither
 * DELIVERED_SEND_STATUSES nor FAILED_SEND_STATUSES below, so `sendOutcome`
 * classifies it 'unknown' on BOTH channels — which is what makes its send chip
 * neutral and its timeline line read as uncertainty. (T608 M4 moved those two
 * lists here from client/components/SendQuote.tsx, where this note used to point,
 * so the send UI and the timeline share one rule.)
 */
export const STATUS_COMPOSED: SendStatus = 'composed';
/** Twilio's initial state on a successful hand-off; the callback advances it. */
export const STATUS_QUEUED: SendStatus = 'queued';
/**
 * The honest terminal state of a successful EMAIL hand-off (T549 M6).
 *
 * Deliberately NOT STATUS_QUEUED: 'queued' is documented above as the state a
 * CALLBACK advances, and there is no Resend delivery webhook in this system, so
 * an email row written 'queued' would read "Queued" to Helen forever. 'accepted'
 * is what actually happened - Resend answered 2xx with a message id - and it
 * stays true with no callback to advance it. See docs/OPEN-QUESTIONS.md (the
 * Resend delivery-webhook follow-up) for the path to a richer status.
 */
export const STATUS_ACCEPTED: SendStatus = 'accepted';
/** OUR bookend: the transport call itself errored — nothing left Twilio. */
export const STATUS_SEND_FAILED: SendStatus = 'send_failed';
/**
 * THE MANUAL-COPY ROW (T524). Helen copied the customer-facing quote message to
 * her clipboard so she could paste it into WhatsApp by hand.
 *
 * What it means: an operator pressed a copy button, and the text that landed on
 * the clipboard is the `message_body` on this row. That is the whole claim.
 *
 * What it deliberately is NOT, and the absences that enforce it:
 *  - NOT evidence of delivery. It is in none of DELIVERED_SEND_STATUSES,
 *    SENT_SEND_STATUSES or FAILED_SEND_STATUSES below, so `sendOutcome` falls
 *    through to 'unknown' on
 *    every channel — a neutral chip and a "Not confirmed" timeline word. There
 *    is no send at all here, let alone a receipt: nothing left this system, and
 *    nothing outside it can ever tell us whether Helen actually pasted it.
 *  - NOT advanceable. It is absent from CALLBACK_STATUSES, so no provider
 *    callback can move a row into it, and the writing site pairs it with a NULL
 *    message_ref so no callback can move it OUT of it either.
 *  - NOT a quote send. The writing site pairs it with a NULL quote_id, which is
 *    what keeps a copy row out of getBookingDetail's `latest_send` — otherwise
 *    the send block's hardcoded "Sent by ..." line would describe a copy.
 *
 * Adding 'whatsapp' to IMPLEMENTED_CHANNELS is a SEPARATE, unrelated decision
 * (the WABA long-pole): this status exists precisely because that driver does
 * not, and it must not be read as a step towards it.
 */
export const STATUS_COPIED: SendStatus = 'copied';

/**
 * Statuses the Twilio status callback may legitimately deliver (D-11
 * allowlist) — the Twilio lifecycle only, never our internal bookends. A
 * callback carrying anything else is rejected, not stored.
 */
export const CALLBACK_STATUSES = [
  'accepted',
  'queued',
  'sending',
  'sent',
  'delivered',
  'undelivered',
  'failed',
] as const satisfies readonly SendStatus[];
export type CallbackStatus = (typeof CALLBACK_STATUSES)[number];

export function isCallbackStatus(v: unknown): v is CallbackStatus {
  return typeof v === 'string' && (CALLBACK_STATUSES as readonly string[]).includes(v);
}

/**
 * WHAT A LEDGER ROW MEANS FOR THE CUSTOMER (T608 M4, R6d; FOURTH VALUE ADDED
 * T1045/board 1962, 2026-09-09). One classification, four outcomes.
 *
 *  - 'delivered': the provider CONFIRMED receipt. A receipt, not a handoff.
 *  - 'sent'     : we handed it to the carrier and NO receipt has come back.
 *  - 'failed'   : terminal, and they did NOT get it.
 *  - 'unknown'  : IN FLIGHT OR UNKNOWABLE. Not "not sent". See below.
 *
 * ⛔ THIS DOCBLOCK SAID "deliberately NO fourth value" UNTIL 2026-09-09, AND THE
 * REASON IT NO LONGER DOES IS THE POINT. That rule was written while 'sent' sat
 * in DELIVERED_SEND_STATUSES, i.e. while "sent" was believed to MEAN delivered.
 * Twilio defines it as "the nearest upstream carrier accepted the outbound
 * message" — a handoff, with no promise a receipt ever follows. Measured in
 * production on message SM9b5b40…: a real customer quote sat at 'sent' with its
 * date_updated never moving off date_sent (no receipt EVER returned) while the
 * kiosk told Helen "Delivered". It had in fact arrived, so the word was
 * accidentally true and never evidenced — the display would have read
 * identically had it never arrived.
 *
 * So the three-bucket rule was sound and its INPUT was wrong. 'sent' is not
 * 'delivered' (nothing confirmed it) and not 'unknown' either (we know exactly
 * where it got to). Collapsing it into 'unknown' would have been the smaller
 * change and was considered; it was rejected because it cannot distinguish
 * "handed to the carrier" from "not sent yet", which is the one fact this
 * incident proved an operator needs.
 *
 * ⚠️ ABSENCE OF 'delivered' IS NOT EVIDENCE OF FAILURE, on this route. Irish
 * multi-segment alphanumeric traffic may never return a receipt at all, so
 * 'sent' is a resting state and not a transient one. Word it accordingly:
 * never as failure, never as confirmation.
 *
 * WHY IT LIVES HERE rather than in the component that had it. The rule was
 * encoded in client/components/SendQuote.tsx as a private chip-tone function,
 * and M4 adds a SECOND reader (the per-booking timeline). Two readers deriving
 * "did the customer get this?" from (status, channel) independently is a defect
 * generator, not duplication: the two screens read the SAME row, so any drift
 * means one of them lies about it. This registry is where the status and channel
 * vocabularies already live, so the interpretation belongs beside them. Callers
 * map the outcome to their own presentation (a chip class, a timeline word) and
 * must never re-derive the outcome itself.
 *
 * 'composed' IS 'unknown', and the direction of that honesty is load-bearing.
 * The pending row is written BEFORE the transport is touched (server/app.ts
 * handleSendQuote) and advanced AFTER it resolves, so a process death in between
 * leaves a permanent 'composed' row for a message the customer DID receive -
 * including a live payable deposit link. Treating it as delivered would be a
 * false promise; treating it as FAILED is worse, because it invites Helen to
 * re-send a quote and re-mint a link the customer already holds. Neither. It
 * falls through to 'unknown' by being absent from both lists below, so a status
 * nobody has classified can never read as good news either.
 */
export const SEND_OUTCOMES = ['delivered', 'sent', 'failed', 'unknown'] as const;
export type SendOutcome = (typeof SEND_OUTCOMES)[number];

/** Terminal states where the customer did NOT get the message. */
export const FAILED_SEND_STATUSES = [
  'failed',
  'undelivered',
  'send_failed',
] as const satisfies readonly SendStatus[];

/** States where the provider CONFIRMED receipt, on EVERY channel. Note
 *  'accepted' is deliberately NOT here - it is channel-dependent, see below.
 *
 *  ⛔ 'sent' WAS IN THIS LIST AND ITS REMOVAL IS THE WHOLE OF board 1962. This
 *  list is a claim about a RECEIPT; 'sent' is a claim about a HANDOFF, and the
 *  two are not the same fact. Putting it back reinstates a screen that tells
 *  Helen "Delivered" for a message no carrier ever acknowledged. */
export const DELIVERED_SEND_STATUSES = [
  'delivered',
] as const satisfies readonly SendStatus[];

/** Handed to the carrier, no receipt back. NOT delivered (nothing confirmed it)
 *  and NOT unknown (we know exactly how far it got). SMS and WhatsApp only in
 *  practice: email's terminal success is 'accepted', and `handleTwilioStatus`
 *  is channel-scoped to TWILIO_CARRIED_CHANNELS, so no email row can reach it.
 *  The classifier arm below is nonetheless channel-BLIND, because the state
 *  means the same thing wherever it appears. */
export const SENT_SEND_STATUSES = [
  'sent',
] as const satisfies readonly SendStatus[];

/**
 * Classify one ledger row. The CHANNEL is required, not optional, because
 * 'accepted' means opposite things on the two of them:
 *  - on EMAIL it is the honest terminal success - Resend answered 2xx with a
 *    message id and there is no delivery webhook in this system to advance it
 *    (see STATUS_ACCEPTED above, and docs/OPEN-QUESTIONS.md Q53);
 *  - on SMS it is Twilio's EARLIEST lifecycle state (accepted -> queued ->
 *    sending -> sent -> delivered|undelivered|failed) and an allowlisted
 *    CALLBACK_STATUSES value, i.e. "Twilio has the request" - the message has
 *    not left for the customer yet and may still fail outright.
 * A channel-blind mapping therefore reports a not-yet-sent SMS as delivered.
 * `channel` is a plain string (send_ledger.channel is TEXT): an unrecognised
 * channel gets the channel-blind answer, which is the conservative one.
 */
export function sendOutcome(deliveryStatus: string, channel: string): SendOutcome {
  if ((FAILED_SEND_STATUSES as readonly string[]).includes(deliveryStatus)) return 'failed';
  // BEFORE the delivered check. That ordering is defensive redundancy, NOT the
  // guarantee: what makes this arm reachable is 'sent' being absent from
  // DELIVERED_SEND_STATUSES. Both, so neither alone has to hold.
  if ((SENT_SEND_STATUSES as readonly string[]).includes(deliveryStatus)) return 'sent';
  if ((DELIVERED_SEND_STATUSES as readonly string[]).includes(deliveryStatus)) return 'delivered';
  // T1988: the team email rides the same Resend transport, so its 'accepted' is
  // the same fact (Resend answered 2xx with a message id).
  if (deliveryStatus === STATUS_ACCEPTED && (channel === CHANNEL_EMAIL || channel === CHANNEL_TEAM_EMAIL)) return 'delivered';
  return 'unknown';
}

/**
 * ⭐ board 2811 M7: WHAT A LEDGER STATUS MEANS FOR A BALANCE TEXT (plan A2 item 6).
 * ⛔ AN EXHAUSTIVE `Record` OVER `SEND_STATUSES`, so a new member fails `tsc` here
 * until somebody decides what it means for Helen's screen:
 *   - `accepted`: the provider took it (`accepted` and `sending` count, as do
 *     `queued`, `sent` and `delivered`);
 *   - `failed`: it did not reach the customer (`FAILED_SEND_STATUSES`);
 *   - `pending`: not decided yet (`composed`); `copied` is the manual WhatsApp
 *     route and is never written for a balance text, so it decides nothing.
 * ⭐ board 2811 R3 (item 2): moved here from core/data/balance-invoices-repo.ts
 * so the ask ladder and the booking's History read ONE map: History calls a
 * `pending` row "not known if it went", never "sent".
 */
const BALANCE_TEXT_LEDGER_OUTCOME: Readonly<Record<SendStatus, 'accepted' | 'failed' | 'pending'>> = {
  composed: 'pending',
  accepted: 'accepted',
  queued: 'accepted',
  sending: 'accepted',
  sent: 'accepted',
  delivered: 'accepted',
  undelivered: 'failed',
  failed: 'failed',
  send_failed: 'failed',
  copied: 'pending',
};

/** A status this build does not know decides nothing (`pending`). */
export function balanceTextLedgerOutcome(status: string): 'accepted' | 'failed' | 'pending' {
  return Object.prototype.hasOwnProperty.call(BALANCE_TEXT_LEDGER_OUTCOME, status)
    ? BALANCE_TEXT_LEDGER_OUTCOME[status as SendStatus]
    : 'pending';
}

/**
 * ⭐ board 2811 rework part 2c (Captain item 13): THE PROVIDER CODES THAT SAY THE
 * NUMBER ITSELF IS WRONG, so a failed text can tell Helen to check the number.
 * Read off `send_ledger.error_code`, which holds a Twilio API error's `code` on a
 * send (core/balance-link-send.ts, server/app.ts), Twilio's `ErrorCode` on a
 * delivery callback, and Infobip's `status.id` on a send
 * (notify/infobip-sms-client.ts) or a callback (server/app.ts).
 * The sources, read 2026-10-07 (re-read the page before adding a code):
 *   - Twilio 21211 "Invalid 'To' Phone Number" and 21614 "'To' number is not a
 *     valid mobile number": https://www.twilio.com/docs/api/errors/21211 and /21614.
 *   - Infobip REJECTED status ids 8 REJECTED_PREFIX_MISSING (prefix or length not
 *     recognised), 51 MISSING_TO (no destination) and 52 REJECTED_DESTINATION
 *     (an invalid destination): the status table at
 *     https://www.infobip.com/docs/essentials/api-essentials/response-status-and-error-codes
 * ⛔ NOT Twilio 30005 or 30006, nor Infobip 9: their documented causes include a
 * phone that is off or out of signal, so "check the number" would be a guess.
 * The two vocabularies cannot collide: Twilio's codes are five digits.
 * SMS ONLY: the email rail writes other codes into the same column.
 */
export const SMS_NUMBER_REJECTED_CODES: ReadonlySet<string> = new Set(['21211', '21614', '8', '51', '52']);

export function isSmsNumberRejected(channel: string, errorCode: string | null | undefined): boolean {
  return channel === CHANNEL_SMS && errorCode != null && SMS_NUMBER_REJECTED_CODES.has(errorCode.trim());
}

/**
 * How the customer prefers to be contacted (P1.7 capture; optional — NULL
 * means "not stated"). 'call' honours the phone-never-closes rule; 'email'
 * matches the inquiry email field; 'sms' is the send channel.
 *
 * T484 (Alex, 2026-07-24): 'whatsapp' is RETIRED from this set. ECMB cannot
 * message a customer on WhatsApp until Meta verification + an approved
 * template sender exist, so offering it promised a channel nothing honours.
 * Deliberately distinct from CHANNELS above, which still carries 'whatsapp'
 * for the OPERATOR alert transport (Helen + Owen) — that one is live and
 * unaffected. Retiring the value here narrows intake only; it never rewrites
 * a preference a customer already stated (see RETIRED_CONTACT_PREFERENCES).
 */
export const CONTACT_PREFERENCES = ['sms', 'call', 'email'] as const;
export type ContactPreference = (typeof CONTACT_PREFERENCES)[number];

export function isContactPreference(v: unknown): v is ContactPreference {
  return typeof v === 'string' && (CONTACT_PREFERENCES as readonly string[]).includes(v);
}

/**
 * Values REFUSED at intake but still readable in the DB, because rows written
 * before the value retired keep it (contact_preference is plain TEXT — no
 * CHECK, no backfill, so history is never rewritten). Read paths must render
 * these; only write paths validate against CONTACT_PREFERENCES.
 */
export const RETIRED_CONTACT_PREFERENCES = ['whatsapp'] as const;

/**
 * Operator-facing wording for a stored contact_preference — Helen's register,
 * not the customer's (the site owns customer voice in InquiryForm's
 * CONTACT_COPY, same two-registers split as the service labels).
 *
 * A retired value renders as what the customer ASKED FOR plus what we can
 * actually do, so Helen is never told a stated preference we cannot honour is
 * simply "text". Unknown/NULL returns null so callers omit the row entirely
 * rather than print a raw enum at her.
 */
const CONTACT_PREFERENCE_LABELS: Record<string, string> = {
  sms: 'Text message',
  call: 'Phone call',
  email: 'Email',
  // Retired: keep the customer's stated intent visible, name the fallback.
  whatsapp: 'WhatsApp (we text or call)',
};

export function contactPreferenceLabel(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  // hasOwn, not a bare index: 'constructor'/'__proto__' would otherwise walk
  // the prototype and return a Function/Object, so `?? null` would never fire
  // and the declared string|null return type would be a lie.
  return Object.hasOwn(CONTACT_PREFERENCE_LABELS, v) ? CONTACT_PREFERENCE_LABELS[v]! : null;
}

/**
 * Machine-readable reasons the customer-send endpoint REFUSES a send (T549 M1).
 *
 * The response keeps its human `error` sentence (several are pinned verbatim by
 * the e2e net and by app.spec.ts, and Sentry/log readers need them); this code
 * is the field the CLIENT switches on. Before it existed, the client sniffed
 * substrings of the server's own sentences (`errText.includes('Stripe')`) and
 * collapsed EVERY 409 to one "no mobile number" message, so three of the four
 * refusal reasons reached Helen as a lie. A code makes the contract explicit and
 * a reworded sentence a copy change instead of a silent behaviour change.
 *
 * 'no_email' and 'email_unconfigured' were declared here by M1 ahead of the
 * branch that emits them; T549 M6 wired the email branch of the send path, so
 * every code in this list is now emitted by handleSendQuote. Declaring the whole
 * set up front kept the client's code->copy map exhaustive under `tsc`, so the
 * milestone that started emitting them could not forget the operator copy.
 *
 * 'unsupported_channel' closes the last hole (T549 M7). M6's 400 was the ONE
 * refusal on this route with no code, which was harmless only while nothing
 * could reach it - no client sent `channel`. The M7 picker sends one, so a
 * codeless 400 would fall through to the generic failure copy and tell Helen
 * "try again in a moment" about a request that can never succeed as posted.
 */
export const SEND_ERROR_CODES = [
  'no_quote',
  'no_amount',
  'no_phone',
  'no_email',
  'deposit_paid',
  'sms_unconfigured',
  'email_unconfigured',
  'stripe_unconfigured',
  'unsupported_channel',
  // T879 — Stripe REFUSED THE KEY (401/402/403), as distinct from `stripe_unconfigured`
  // (no key at all) and from a transient provider fault. The two are opposite
  // instructions and only one of them is ever right: an unconfigured kiosk and a
  // wobbling Stripe both resolve themselves, so "try again in a moment" is honest
  // copy for them; a REJECTED key is a permanent configuration fault that fails
  // identically on every booking until a human changes the key, so telling Helen to
  // retry sends her round a loop that cannot terminate.
  //
  // Measured, which is why this code exists: on 2026-08-07 the live kiosk carried a
  // key that had been pasted TWICE (214 chars = 2 x 107), so every mint 401'd. Every
  // surface reported it as "could not be copied - try again in a moment", and the
  // diagnosis went looking at the clipboard and at restricted-key SCOPES (a 403
  // story) before anyone read the status code. This code is what makes that
  // measurement legible from Helen's screen without reading container logs.
  'stripe_auth_failed',
  // T1002 M4 — THE REFUND WAS RECORDED AND ITS OUTCOME IS UNKNOWN. Distinct from
  // every code above because it is the only one where the honest instruction is
  // "do NOT try again": the other failures moved nothing, so a retry is free,
  // whereas here the money may already be on its way back to the customer and a
  // second attempt mints a new refund row, a new idempotency key and therefore a
  // SECOND REAL REFUND.
  //
  // ⛔ It is deliberately NOT reported as a plain failure. The local row is left
  // non-terminal on purpose (an outcome nobody observed is not a failure), and
  // "could not refund - try again in a moment" would be a lie about a request
  // that may well have succeeded.
  'refund_outcome_unknown',
  // T1447 M5 — THIS BOOKING IS BILLED TO AN ACCOUNT, so it takes no card deposit
  // (Alex, 2026-08-19: consumers pay a deposit, accounts are invoiced monthly,
  // the two rails never meet). Its own code for the same reason
  // 'stripe_auth_failed' has one: it is PERMANENT. Every other refusal on these
  // routes describes something that can change (add a phone, price the quote,
  // fix the key), so "try again" is at worst wasted; this one is a property of
  // the booking that a retry can never alter, and the only honest instruction is
  // to bill it the account way.
  //
  // ⛔ NOT folded into an existing code, and 'no_phone' is the one it would have
  // been folded into. Two comments in server/app.ts justified themselves with
  // "an account booking has no customer at all" and answered the case with the
  // existing no-contact refusals — but that premise is FALSE (account_id and
  // customer_id are independent nullable columns with no CHECK between them, and
  // the live Door B path retro-links an account onto a booking whose customer has
  // a phone), so those refusals never fired. Telling Helen "no mobile number"
  // about a booking that has one sends her to edit a customer record that is
  // already correct, and the deposit she must not take stays takeable.
  'account_booking',
  // board 2089 M2 — THE SENDER CANNOT REACH THIS NUMBER'S COUNTRY, so nothing
  // was posted to the transport at all (notify/sms-reach.ts holds the table and
  // the reasoning). Its own code because the instruction it carries is not one
  // any existing refusal gives.
  //
  // ⛔ IT IS PERMANENT FOR US / CA / BE: alphanumeric senders are not supported
  // there at all, so this is a property of the NUMBER that no retry and no
  // edit to the booking can change. It is ENV-DEPENDENT for the
  // pre-registration countries (AU / ES / AT), where it lifts the day the
  // sender is registered and SMS_SENDER_REGISTERED_COUNTRIES names them. Both
  // readings leave the same one honest instruction for Helen today, which is
  // why they share a code: send the link by email.
  //
  // ⛔ NOT FOLDED INTO 'no_phone'. The booking HAS a mobile number and it is
  // correct; telling Helen otherwise sends her to edit a customer record that
  // is already right, which is the exact mistake 'account_booking' was split
  // out of 'no_phone' to stop repeating.
  'sms_unreachable_country',
  // board 2089 rework 1 - THE STORED NUMBER COULD NOT BE READ, so there is no
  // country to grade at all (`smsReachability`'s `no_country`: a legacy
  // mis-prefixed `+3537400123456`, or a non-geographic `+800...`). Its own code
  // because its remedy is the OPPOSITE of the one above: correct the number
  // with its country, rather than switch channel and leave a number that can
  // never be texted sitting on the record exactly as it was.
  'sms_unreadable_number',
  // ⭐ board 2814 M4 (plan DD-5, S8), APPENDED, never re-ordered — THIS RUN IS CASH, PAID TO THE DRIVER ON THE
  // DAY (`bookings.payer_kind = 'self'`). PERMANENT, like 'account_booking', and
  // its own code because the instruction differs: nothing is taken online and the
  // home is never billed; the driver collects the fare. Checked FIRST by
  // `assertDepositRailOpen`, so a cash run on the home's account says these words
  // rather than the account's. If the payer was mis-tapped, "Change who pays" on
  // the booking corrects it.
  'cash_to_driver',
  // ⭐ board 2814 M7 (plan DD-7), APPENDED, never re-ordered — THIS RUN WAS PASSED
  // TO A TAXI FIRM (`bookings.taxi_passed_at`): nothing is taken online for it.
  // Checked FIRST by `assertDepositRailOpen`, before the cash and account reasons.
  // Lifted only by "Undo" on the booking.
  'taxi_passed',
] as const;
export type SendErrorCode = (typeof SEND_ERROR_CODES)[number];

/** Boundary guard: the client reads `code` off an untrusted response body. */
export function isSendErrorCode(v: unknown): v is SendErrorCode {
  return typeof v === 'string' && (SEND_ERROR_CODES as readonly string[]).includes(v);
}

/**
 * T1002 M4 — WHAT A HUMAN IS TOLD WHEN A REFUND'S OUTCOME IS UNKNOWN.
 *
 * ⛔ THE ONE THING THIS SENTENCE MUST NOT SAY IS "try again", and it lives HERE,
 * in the one module both sides import, because that rule was broken the moment
 * there were two copies of it. Measured by all three of this milestone's review
 * gates: the client copy said "Do not try again" while the SERVER's 502 body
 * said "Check the payment in Stripe BEFORE TRYING AGAIN" — under a comment
 * asserting that it did not — and since nothing under src/client calls the
 * refund route, the server string was the only one a human could ever read. On
 * the one route in this service where a retry is a SECOND REAL REFUND, the live
 * string carried the opposite of the instruction the code was arguing for.
 *
 * So there is one string, not two that agree today. Every other failure on the
 * money path moved nothing, which is why "try again in a moment" is honest copy
 * for them and a lie here: the refund may already have gone through, and asking
 * again mints a new refund row with its own idempotency key, i.e. a second real
 * refund out of the business bank account.
 *
 * It carries no status code, no provider text and no amount (SEC-5), and it does
 * not say "failed", because that is precisely what we do not know.
 */
export const REFUND_OUTCOME_UNKNOWN_COPY =
  'The refund was recorded but Stripe did not confirm it. ' +
  'Do not try again - check the payment in Stripe first, because it may already have gone through.';

/**
 * board 2089 M2 — WHAT HELEN READS WHEN THE SENDER CANNOT REACH THE COUNTRY.
 *
 * Alex's wording, verbatim, and it is not a gate author's to reword. It states
 * the one action that works rather than describing the fault, because the
 * refusal is permanent for an unsupported country and "try again in a moment"
 * would send her round a loop that cannot terminate.
 *
 * ⛔ ONE STRING FOR BOTH SIDES, on the REFUND_OUTCOME_UNKNOWN_COPY precedent
 * directly above. The route's 409 body and the operator copy in
 * `client/lib/actions.ts` are the same sentence, so they cannot disagree the
 * way the refund pair did. ASCII hyphen: `scripts/check-copy.sh` bans dash
 * characters across the client, shared and notify roots.
 */
export const SMS_UNREACHABLE_COUNTRY_COPY =
  'SMS not available for this country - send the link by email';

/**
 * board 2089 rework 1 - WHAT HELEN READS WHEN THE STORED NUMBER CANNOT BE READ.
 *
 * `smsReachability` answers `no_country` for a number it cannot place in any
 * country: a legacy mis-prefixed `+3537400123456`, or a non-geographic
 * `+800...`. The sentence above would send Helen to email about it, when the
 * fix is to re-key the number under its country. So this one names that
 * action and promises nothing after it. Today re-entering means re-keying the
 * job as a walk-in (no correction route; Q148).
 *
 * ONE STRING FOR BOTH SIDES, on the sentence above's rule: the route's 409 body
 * and the operator copy in `client/lib/actions.ts`. Held to the same pin: a
 * spaced ASCII hyphen, no dash characters, no digits, no trailing period.
 */
export const SMS_UNREADABLE_NUMBER_COPY =
  'This number could not be read - re-enter it with its country';

/**
 * ⭐⭐ BOARD 2089 M3 (R-6) — WHY NO TEXT CAN GO TO THIS CUSTOMER, IN ONE SENTENCE,
 * ON EVERY SCREEN THAT CAN SEE THE CONDITION.
 *
 * A refused number stores as NULL with a fixed-enum reason (`shared/phone.ts`),
 * and before this the kiosk simply showed the typed string, or nothing at all, on
 * both customer read-backs. Helen then rang a customer whose quote SMS had never
 * left, with no way to tell that from one that had.
 *
 * ⛔ ONE BUILDER, TWO CALLERS, on the `contactPreferenceLabel` precedent above:
 * `components/CustomerInfoBlock.tsx` (which holds the typed string AND the
 * country it was read under) and `screens/BookingDetail.tsx` (which holds
 * neither, only the null). Two hand-written sentences for one condition is how
 * two screens end up telling her different things about the same booking.
 *
 * ⚠️ `null` IS NOT "no country", IT IS "NO COUNTRY KNOWN HERE", and the two
 * wordings differ because the facts do: a caller that knows the country can say
 * which rule the number failed, and a caller that does not must not invent one.
 * The absence arm also never says "refused" — a customer who gave no number at
 * all leaves the identical null, and both have the same remedy.
 *
 * ⚠️ NO PHONE-CORRECTION ROUTE EXISTS TODAY (`core/data/customers-repo.ts`'s only
 * UPDATE is a fill-if-null), so "corrected" means ringing the customer and
 * re-keying the booking as a walk-in. Recorded as an open question, not implied.
 *
 * ASCII hyphen, on `SMS_UNREACHABLE_COUNTRY_COPY`'s rule above.
 */
export function phoneRefusalCopy(countryName: string | null): string {
  return countryName === null
    ? 'No usable number on file - no text can be sent until it is corrected'
    : `Not recognised as a ${countryName} number - no text can be sent until it is corrected`;
}

/**
 * THE recipient-email precedence (T549 M8). One expression, four call sites:
 * the send path itself (server/app.ts), the two store writes that mirror it
 * (client/lib/actions.ts) and the booking-detail send surface
 * (client/screens/BookingDetail.tsx).
 *
 * Shared because a screen that names a different recipient from the one the
 * send uses is a defect in both directions - promising an address the server
 * will not use, or telling Helen there is no email while the send would have
 * gone through - and because the four copies had already drifted on the empty
 * string (`.trim() ||` on the server vs `??` on the client), which is exactly
 * the divergence that is invisible until it matters.
 *
 * Order is the security property, not a preference:
 *   1. `customers.email` - the stored customer record. Only an operator-sourced
 *      path may ever write it (customers-repo.ts), so it is the business's own
 *      record of where this customer is reached, and it WINS whenever it exists.
 *   2. `inquiryEmail` - the address supplied on THIS booking's own inquiry.
 *      Unauthenticated input, scoped to the one booking that inquiry created,
 *      and the only address we have for a customer whose stored row has none.
 * Reversing it would let a web-form submission REDIRECT a quote (and its
 * deposit link) away from the address the business holds.
 *
 * Blank is absent, on BOTH inputs: send_ledger.target_e164 and the customers
 * columns are plain TEXT, which happily admit '   ', and a whitespace-only
 * "address" must never pass a recipient gate and be ledgered as a real send.
 *
 * `fromInquiry` rides along rather than being re-derived at each screen: the
 * resolved address alone cannot say WHERE it came from, and the screens have to
 * (Helen is the only control on "is this really the customer's address?"). One
 * return value means the label and the recipient can never disagree.
 */
export function resolveRecipientEmail(
  customerEmail: string | null | undefined,
  inquiryEmail: string | null | undefined,
): { email: string | null; fromInquiry: boolean } {
  const stored = customerEmail?.trim() || null;
  const supplied = inquiryEmail?.trim() || null;
  return stored
    ? { email: stored, fromInquiry: false }
    : { email: supplied, fromInquiry: supplied !== null };
}
